
import React, { useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, functions, httpsCallable } from "/Users/michaeltucker/Documents/coding-projects/client/src/firebase.js";

import {
  Button,
  Label,
  FormGroup,
  Form,
  Input,
} from "reactstrap";
import AuthLoginMobileLogo from "components/AuthLoginMobileLogo";
import GoogleSignInButton from "components/GoogleSignInButton";
import {
  AUTH_PROVIDER_IDS,
  EXISTING_PASSWORD_ACCOUNT_MESSAGE,
  GOOGLE_LINK_SUCCESS_MESSAGE,
  GOOGLE_LOGIN_NO_ACCOUNT_MESSAGE,
  clearPendingGoogleLinkEmail,
  getDuplicateAccountEmail,
  getGoogleAuthErrorMessage,
  getGoogleRedirectResult,
  hasUnlinkedPasswordAccountForEmail,
  isDuplicateAccountError,
  linkPendingGoogleAfterPasswordSignIn,
  logDuplicateAccountAttempt,
  rejectNewGoogleUserOnLogin,
  shouldLinkPendingGoogleForEmail,
  signInWithGooglePopupOrRedirect,
} from "utils/authProviderMethods";
import { logAuthAuditEvent, trackAuthAnalyticsEvent } from "utils/authTelemetry";
import { attachStoredReferralAfterSignup } from "utils/referralSignup";
import {
  buildAttributionSummary,
  captureAdAttribution,
  getSignupAttribution,
  inferLeadSourceFromAttribution,
  trackRegistrationSuccess,
} from "utils/metaPixel";
import {
  bootstrapUserShellAfterSignup,
  ensureUserDocumentForAuthUser,
  ensureUserDocumentWithRetries,
} from "utils/userProvisioning";
import { reportAppError } from "utils/reportAppError";
import { captureSignupCountryAfterRegistration } from "utils/captureSignupCountry";
import { buildSignupCountrySignals } from "utils/countryAttribution";
import { FULLSTORY_ELEMENTS } from "utils/fullstoryElements";
import { resolvePostLoginRedirectPath } from "utils/loginRedirect";
import { persistEmailReactivationClickIfNeeded } from "utils/emailReactivationAttribution";
import {
  consumeContinueSetupReturnPath,
  getMobileSignupRedirectPath,
} from "utils/mobileSignupHandoff";

const AUTH_BOOTSTRAP_SESSION_KEY = "authBootstrapPending";

function getLoginErrorMessage(error) {
  switch (error?.code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact support if you need help.";
    case "auth/user-not-found":
      return "No account found with this email. Check the address or create an account.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return "Incorrect email or password. Please try again.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please wait a moment and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/missing-password":
      return "Please enter your password.";
    default:
      return "Unable to sign in right now. Please try again.";
  }
}

function getPasswordResetErrorMessage(error) {
  const code = error?.code || "";
  switch (code) {
    case "functions/invalid-argument":
    case "auth/invalid-email":
    case "auth/missing-email":
      return "Please enter a valid email address.";
    case "functions/resource-exhausted":
    case "auth/too-many-requests":
      return "Too many requests. Please wait a moment and try again.";
    case "functions/unavailable":
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return error?.message && !code.startsWith("functions/")
        ? "Unable to send a reset link right now. Please try again."
        : "Unable to send a reset link right now. Please try again.";
  }
}

function Login() {
  useEffect(() => {
    document.body.classList.add("login-page");
    return () => document.body.classList.remove("login-page");
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetSendSuccess, setResetSendSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginSuccess, setLoginSuccess] = useState("");
  const [resetError, setResetError] = useState("");
  const [isResetSending, setIsResetSending] = useState(false);
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [storedContinueSetupPath] = useState(() => consumeContinueSetupReturnPath());
  const redirectPath = storedContinueSetupPath
    || resolvePostLoginRedirectPath({ location, fallback: "/" });

  const resetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    const normalizedEmail = email?.trim();
    if (!normalizedEmail) {
      setResetError("Please enter your email address.");
      return;
    }
    setIsResetSending(true);
    try {
      const requestPasswordReset = httpsCallable(functions, "requestPasswordReset");
      await requestPasswordReset({
        email: normalizedEmail,
        continueOrigin: window.location.origin,
      });
      setResetSendSuccess(true);
    } catch (error) {
      setResetError(getPasswordResetErrorMessage(error));
    } finally {
      setIsResetSending(false);
    }
  };

  const finishGoogleSignIn = async (authUser, source) => {
    const signupAttribution = getSignupAttribution();
    const countrySignals = buildSignupCountrySignals();
    const { created, profile } = await ensureUserDocumentForAuthUser(authUser, {
      email: authUser?.email,
      firstName: authUser?.displayName ? undefined : "",
      lastName: "",
      leadSource: inferLeadSourceFromAttribution() || "Google Sign-In",
      attribution: signupAttribution || null,
      attributionSummary: buildAttributionSummary(signupAttribution),
      countryAttribution: {
        browserLocale: countrySignals.browserLocale || null,
        browserTimezone: countrySignals.browserTimezone || null,
      },
    });

    if (created) {
      await attachStoredReferralAfterSignup();
      await captureSignupCountryAfterRegistration();
      await trackRegistrationSuccess({
        uid: authUser.uid,
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        triggerSource: "login_google_new_account",
        registrationMethod: "google",
      });
      trackAuthAnalyticsEvent("signup_google", {
        providerId: AUTH_PROVIDER_IDS.GOOGLE,
        source,
      });
      await bootstrapUserShellAfterSignup(dispatch, authUser.uid);
    } else {
      trackAuthAnalyticsEvent("login_google", {
        providerId: AUTH_PROVIDER_IDS.GOOGLE,
        source,
      });
    }

    await persistEmailReactivationClickIfNeeded();
    history.push(created ? getMobileSignupRedirectPath() : redirectPath);
  };

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginSuccess("");
    sessionStorage.setItem(AUTH_BOOTSTRAP_SESSION_KEY, "true");

    try {
      const authUser = await signInWithEmailAndPassword(auth, email, password);
      if (shouldLinkPendingGoogleForEmail(authUser.user.email)) {
        try {
          const linkResult = await linkPendingGoogleAfterPasswordSignIn("login");
          if (linkResult.redirected) {
            return;
          }

          setLoginSuccess(GOOGLE_LINK_SUCCESS_MESSAGE);
          window.setTimeout(() => history.push(redirectPath), 1600);
          return;
        } catch (linkError) {
          trackAuthAnalyticsEvent("provider_link_failure", {
            providerId: AUTH_PROVIDER_IDS.GOOGLE,
            source: "login",
            reason: linkError?.code || "unknown",
          });
          setLoginError(getGoogleAuthErrorMessage(linkError));
          return;
        }
      }

      // Heal Auth→usersNew orphans from failed client provisioning.
      try {
        const signupAttribution = getSignupAttribution();
        const countrySignals = buildSignupCountrySignals();
        const { created, profile } = await ensureUserDocumentWithRetries(authUser.user, {
          email: authUser.user?.email,
          leadSource: inferLeadSourceFromAttribution() || "",
          attribution: signupAttribution || null,
          attributionSummary: buildAttributionSummary(signupAttribution),
          countryAttribution: {
            browserLocale: countrySignals.browserLocale || null,
            browserTimezone: countrySignals.browserTimezone || null,
          },
        });

        if (created) {
          await attachStoredReferralAfterSignup();
          await captureSignupCountryAfterRegistration();
          await trackRegistrationSuccess({
            uid: authUser.user.uid,
            email: profile.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            triggerSource: "password_login_heal",
            registrationMethod: "email_password",
          });
          await bootstrapUserShellAfterSignup(dispatch, authUser.user.uid);
        }
      } catch (provisionError) {
        reportAppError(provisionError, {
          feature: "user-provisioning",
          action: "password-login-heal",
          uid: authUser.user?.uid,
          dedupeKey: `password-login-heal:${authUser.user?.uid || "unknown"}`,
        });
        // Continue into the app; Auth onCreate / bootstrap heal may still recover.
      }

      await persistEmailReactivationClickIfNeeded();
      history.push(redirectPath);
    } catch (error) {
      sessionStorage.removeItem(AUTH_BOOTSTRAP_SESSION_KEY);
      setLoginError(getLoginErrorMessage(error));
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError("");
    setLoginSuccess("");
    setIsGoogleAuthLoading(true);

    try {
      if (email && await hasUnlinkedPasswordAccountForEmail(email)) {
        await logDuplicateAccountAttempt({
          email,
          source: "login",
          reason: "password_method_preflight",
        });
        setLoginError(EXISTING_PASSWORD_ACCOUNT_MESSAGE);
        return;
      }

      sessionStorage.setItem(AUTH_BOOTSTRAP_SESSION_KEY, "true");
      const { result, redirected } = await signInWithGooglePopupOrRedirect("login");
      if (redirected) {
        return;
      }

      // Firebase Google auth creates users when none exist — block that on Login.
      await rejectNewGoogleUserOnLogin(result, "login");
      await finishGoogleSignIn(result.user, "login");
    } catch (error) {
      sessionStorage.removeItem(AUTH_BOOTSTRAP_SESSION_KEY);
      if (isDuplicateAccountError(error)) {
        await logDuplicateAccountAttempt({
          email: getDuplicateAccountEmail(error) || email,
          source: "login",
          reason: error.code,
        });
      }
      setLoginError(getGoogleAuthErrorMessage(error));
    } finally {
      setIsGoogleAuthLoading(false);
    }
  };

  useEffect(() => {
    captureAdAttribution(window.location.search);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const finishRedirectLogin = async () => {
      try {
        const { result, intent } = await getGoogleRedirectResult();
        if (!result || !["login", "link"].includes(intent)) {
          return;
        }

        if (intent === "link") {
          clearPendingGoogleLinkEmail();
          trackAuthAnalyticsEvent("provider_link_success", {
            providerId: AUTH_PROVIDER_IDS.GOOGLE,
            source: "login_redirect",
          });
          await logAuthAuditEvent("auth_provider_linked", {
            providerId: AUTH_PROVIDER_IDS.GOOGLE,
            source: "login_redirect",
          });
          setLoginSuccess(GOOGLE_LINK_SUCCESS_MESSAGE);
          window.setTimeout(() => history.push(redirectPath), 1600);
          return;
        }

        // Firebase Google auth creates users when none exist — block that on Login.
        await rejectNewGoogleUserOnLogin(result, "login_redirect");
        await finishGoogleSignIn(result.user, "login_redirect");
      } catch (error) {
        if (!isMounted) return;
        sessionStorage.removeItem(AUTH_BOOTSTRAP_SESSION_KEY);
        if (isDuplicateAccountError(error)) {
          await logDuplicateAccountAttempt({
            email: getDuplicateAccountEmail(error) || email,
            source: "login_redirect",
            reason: error.code,
          });
        }
        setLoginError(getGoogleAuthErrorMessage(error));
      }
    };

    finishRedirectLogin();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (resetSendSuccess) setResetSendSuccess(false);
    if (loginError) setLoginError("");
    if (loginSuccess) setLoginSuccess("");
    if (resetError) setResetError("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (loginError) setLoginError("");
    if (loginSuccess) setLoginSuccess("");
  };

  const openPasswordReset = () => {
    setLoginError("");
    setShowPasswordReset(true);
  };

  const backToSignIn = () => {
    setResetError("");
    setShowPasswordReset(false);
    setResetSendSuccess(false);
  };

  if (!showPasswordReset) {
    return (
      <Form onSubmit={login} className="auth-login-form" noValidate>
        <div className="auth-login-card">
          <AuthLoginMobileLogo />
          <h1 className="auth-login-title">Welcome back!</h1>
          <p className="auth-login-subtitle">Sign in to your account.</p>

          {loginError && (
            <div className="auth-login-error-note" role="alert">
              <p>{loginError}</p>
              {loginError === GOOGLE_LOGIN_NO_ACCOUNT_MESSAGE && (
                <p>
                  <Link
                    to="/auth/register"
                    className="auth-login-footer-link"
                    data-fs-element={FULLSTORY_ELEMENTS.authentication.switchToRegister}
                  >
                    Create an account
                  </Link>
                </p>
              )}
            </div>
          )}

          {loginSuccess && (
            <div className="auth-login-success-note" role="status">
              {loginSuccess.split("\n").map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          )}

          <FormGroup className="auth-login-field">
            <Label for="login-email">Email</Label>
            <Input
              id="login-email"
              value={email}
              onChange={handleEmailChange}
              placeholder="you@school.edu"
              type="email"
              autoComplete="email"
              className="auth-login-input"
            />
          </FormGroup>

          <FormGroup className="auth-login-field">
            <Label for="login-password">Password</Label>
            <div className="auth-login-password-wrap">
              <Input
                id="login-password"
                value={password}
                onChange={handlePasswordChange}
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="auth-login-input auth-login-input--with-toggle fs-mask"
              />
              <button
                type="button"
                className="auth-login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`} />
              </button>
            </div>
          </FormGroup>

          <button
            type="button"
            className="auth-login-link-button auth-login-forgot"
            onClick={openPasswordReset}
            data-fs-element={FULLSTORY_ELEMENTS.authentication.forgotPassword}
          >
            Forgot password?
          </button>

          <Button
            type="submit"
            className="btn design-system-primary-solid auth-login-submit"
            data-fs-element={FULLSTORY_ELEMENTS.authentication.signIn}
          >
            Sign in
          </Button>

          <div className="auth-login-divider">
            <span>or</span>
          </div>

          <GoogleSignInButton
            onClick={handleGoogleLogin}
            loading={isGoogleAuthLoading}
            data-fs-element={FULLSTORY_ELEMENTS.authentication.googleSignIn}
          />

          <p className="auth-login-footer-text">
            Don&apos;t have an account?{" "}
            <Link
              to="/auth/register"
              className="auth-login-footer-link"
              data-fs-element={FULLSTORY_ELEMENTS.authentication.switchToRegister}
            >
              Create one
            </Link>
          </p>
        </div>
      </Form>
    );
  }

  return (
    <Form onSubmit={resetPassword} className="auth-login-form" noValidate>
      <div className="auth-login-card">
        <AuthLoginMobileLogo />
        <button
          type="button"
          className="auth-login-back-link"
          onClick={backToSignIn}
        >
          <i className="fa fa-arrow-left" aria-hidden="true" />
          Back to sign in
        </button>

        <h1 className="auth-login-title">Reset password</h1>
        <p className="auth-login-subtitle">
          Enter your email and we&apos;ll send you a reset link from
          auth@send.lessonlaunchpad.com.
        </p>

        {resetError && (
          <div className="auth-login-error-note" role="alert">
            {resetError}
          </div>
        )}

        <FormGroup className="auth-login-field">
          <Label for="reset-email">Email</Label>
          <Input
            id="reset-email"
            value={email}
            onChange={handleEmailChange}
            placeholder="you@school.edu"
            type="email"
            autoComplete="email"
            className="auth-login-input"
            disabled={isResetSending || resetSendSuccess}
          />
        </FormGroup>

        <Button
          type="submit"
          className="btn design-system-primary-solid auth-login-submit"
          data-fs-element={FULLSTORY_ELEMENTS.authentication.forgotPassword}
          disabled={isResetSending || resetSendSuccess}
        >
          {isResetSending ? "Sending…" : "Send reset link"}
        </Button>

        {resetSendSuccess && (
          <div className="auth-login-success-note">
            <p>
              If an account exists for that email, you&apos;ll get a message from
              auth@send.lessonlaunchpad.com. Check spam if you don&apos;t see it
              within a couple of minutes.
            </p>
            <p>
              Usually sign in with Google? Use <strong>Sign in with Google</strong>{" "}
              on the login page instead — you may not need a password.
            </p>
            <button
              type="button"
              className="auth-login-link-button"
              onClick={() => window.location.reload()}
            >
              Return to sign in
            </button>
          </div>
        )}
      </div>
    </Form>
  );
}

export default Login;
